import { t, UUID, DateTimeISO } from '../common.ts';

export const GameType = t.Union([
  t.Literal('AGGREGATOR'), // multi-vertical (Evolution, Pragmatic) — provider only
  t.Literal('CASINO'),
  t.Literal('SLOT'),
  t.Literal('SPORT'),
  t.Literal('LOTTO'),
  t.Literal('FISHING'),
  t.Literal('CARD'),
  t.Literal('ETC'),
]);

export const LaunchMode = t.Union([
  t.Literal('iframe'),
  t.Literal('redirect'),
  t.Literal('popup'),
  // in-house Originals games render as a native player-app route, not an external URL
  t.Literal('internal'),
]);
export type LaunchMode = 'iframe' | 'redirect' | 'popup' | 'internal';

export const LaunchMethod = t.Union([t.Literal('GET'), t.Literal('POST')]);
export type LaunchMethod = 'GET' | 'POST';

export const ProviderAuthType = t.Union([
  t.Literal('token'),
  t.Literal('username+password'),
  t.Literal('signed_url'),
  t.Literal('jwt'),
  t.Literal('hmac'),
  // in-house Originals games launch without provider-side auth
  t.Literal('none'),
]);

export const ProviderStatus = t.Union([
  t.Literal('active'),
  t.Literal('inactive'),
  t.Literal('archived'),
]);

export const ProviderView = t.Object(
  {
    id: UUID,
    aggregatorId: t.Union([t.String(), t.Null()]),
    code: t.String(),
    name: t.String(),
    displayName: t.String(),
    type: GameType,
    logoUrl: t.Union([t.String(), t.Null()]),
    launchMode: LaunchMode,
    launchMethod: t.Optional(LaunchMethod),
    authType: ProviderAuthType,
    currency: t.String(),
    seamlessEnabled: t.Boolean(),
    maintenance: t.Boolean(),
    sortOrder: t.Integer(),
    status: ProviderStatus,
    createdAt: DateTimeISO,
    brandColor: t.Optional(t.Union([t.String(), t.Null()])),
    categories: t.Array(t.String()),
  },
  { $id: 'ProviderView' },
);

export const CreateProviderRequest = t.Object(
  {
    code: t.String({ minLength: 2, maxLength: 32 }),
    name: t.String(),
    displayName: t.String(),
    type: GameType,
    aggregatorId: t.Optional(t.String()),
    launchMode: LaunchMode,
    launchMethod: LaunchMethod,
    authType: ProviderAuthType,
    apiBaseUrl: t.String({ format: 'uri' }),
    callbackIpWhitelist: t.Array(t.String(), { default: [] }),
    currency: t.String({ default: 'THB' }),
    seamlessEnabled: t.Boolean({ default: true }),
    brandColor: t.Optional(t.String()),
    categories: t.Optional(t.Array(t.String())),
    logoUrl: t.Optional(t.String()),
  },
  { $id: 'CreateProviderRequest' },
);

/**
 * Partial update body. Deliberately NOT t.Partial(CreateProviderRequest):
 * that would inherit the TypeBox `default` annotations and Elysia would
 * inject them into every PATCH body, silently resetting currency /
 * seamlessEnabled / callbackIpWhitelist on unrelated partial updates.
 * Also carries the operational fields (maintenance, sortOrder, status)
 * that create deliberately omits.
 */
export const UpdateProviderRequest = t.Partial(
  t.Object({
    code: t.String({ minLength: 2, maxLength: 32 }),
    name: t.String(),
    displayName: t.String(),
    type: GameType,
    aggregatorId: t.Optional(t.String()),
    launchMode: LaunchMode,
    launchMethod: LaunchMethod,
    authType: ProviderAuthType,
    apiBaseUrl: t.String({ format: 'uri' }),
    callbackIpWhitelist: t.Array(t.String()),
    currency: t.String(),
    seamlessEnabled: t.Boolean(),
    brandColor: t.String(),
    categories: t.Array(t.String()),
    logoUrl: t.String(),
    maintenance: t.Boolean(),
    sortOrder: t.Integer(),
    status: ProviderStatus,
  }),
);

export const GameView = t.Object(
  {
    id: UUID,
    providerId: UUID,
    code: t.String(),
    name: t.String(),
    imageUrl: t.Union([t.String(), t.Null()]),
    gameType: GameType,
    tag: t.Union([t.Literal('new'), t.Literal('hot'), t.Null()]),
    rtp: t.Union([t.Number(), t.Null()]),
    launchCode: t.String(),
    status: t.Union([t.Literal('active'), t.Literal('inactive')]),
    sortOrder: t.Integer(),
    // Provider launch dispatch — denormalised onto each game so the FE can
    // pick iframe vs redirect vs popup at click time without an extra hop.
    providerCode: t.Optional(t.String()),
    providerDisplayName: t.Optional(t.String()),
    launchMode: t.Optional(LaunchMode),
    launchMethod: t.Optional(LaunchMethod),
  },
  { $id: 'GameView' },
);

export const LaunchGameRequest = t.Object(
  {
    providerId: UUID,
    gameId: UUID,
    lang: t.String({ default: 'th' }),
    returnUrl: t.Optional(t.String({ format: 'uri' })),
  },
  { $id: 'LaunchGameRequest' },
);

export const LaunchGameResponse = t.Object(
  {
    mode: LaunchMode,
    url: t.String({ format: 'uri' }),
    method: LaunchMethod,
    payload: t.Union([t.Record(t.String(), t.Unknown()), t.Null()]),
    expiresAt: DateTimeISO,
  },
  { $id: 'LaunchGameResponse' },
);
